import React, { useState } from 'react';
import { useLanguage } from '../LanguageContext';
import { motion } from 'framer-motion';
import { Download, Share2, RefreshCw, Sparkles, QrCode, Maximize2, X, Printer, Layout, Frame, Type, Edit, Wand2 } from 'lucide-react';
import { Button } from '../ui/button';
import ShareModal from './ShareModal';
import ImageComparison from '../ImageComparison';
import PrintConfigModal from './PrintConfigModal';

export default function ResultDisplay({
  originalImage,
  generatedImage,
  characterName,
  onTryAnother,
  isLoading,
  overlayText,
  watermarkText,
  logoUrl,
  onEditFrame,
  generationProgress = 0,
  currentStage = '',
}) {
  const { t, language } = useLanguage();
  const [showShareModal, setShowShareModal] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [isZoomOpen, setIsZoomOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [viewMode, setViewMode] = useState('split'); // 'split' or 'compare'
  // Shape of the finished image, so a landscape frame isn't letterboxed in a portrait box.
  const [resultAspect, setResultAspect] = useState(null);
  // The event's frame is applied upstream in Photobooth.jsx (finalImage).
  // Here, the generatedImage prop is already the final framed result.
  const displayImage = generatedImage;

  const handleDownload = async () => {
    if (!displayImage) return;
    await downloadImage(displayImage, `ai-photobooth-${characterName || 'result'}.jpg`);
  };

  const handleDownloadOriginal = async () => {
    if (!originalImage) return;
    await downloadImage(originalImage, `photobooth-original-${Date.now()}.jpg`);
  };

  const downloadImage = async (src, filename) => {
    try {
      const response = await fetch(src);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('Download failed:', err);
      alert(language === 'he' ? 'שגיאה בהורדת התמונה' : 'Download failed');
    }
  };

  const handleShare = async () => {
    if (!generatedImage) return;
    
    if (navigator.share) {
      try {
        const response = await fetch(displayImage);
        const blob = await response.blob();
        const file = new File([blob], 'ai-photobooth.jpg', { type: 'image/jpeg' });

        await navigator.share({
          title: 'AI PhotoBooth',
          text: language === 'he' ? 'תראו מה יצרתי!' : 'Check out my AI transformation!',
          files: [file]
        });
      } catch (err) {
        console.error('Share failed:', err);
      }
    }
  };

  const handlePrint = () => {
    if (!displayImage) return;
    // Open the full config modal (printer + paper size + copies + optional Windows dialog)
    setShowPrintModal(true);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="relative">
          {/* Animated Background Circle */}
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
            className="w-24 h-24 rounded-full border-4 border-electric-purple/20 border-t-electric-purple mb-6"
          />
          
          {/* Middle Circle */}
          <motion.div
            animate={{ rotate: -360 }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            className="absolute inset-2 w-20 h-20 rounded-full border-4 border-electric-blue/30 border-t-electric-blue"
          />
          
          {/* Center Circle with Progress */}
          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute inset-4 w-16 h-16 rounded-full bg-gradient-to-br from-electric-blue to-electric-purple flex items-center justify-center"
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              className="relative"
            >
              <Sparkles className="w-8 h-8 text-white" />
              {/* Progress Percentage */}
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xs font-bold text-white">
                  {generationProgress}%
                </span>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Progress Text */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <motion.div
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="flex items-center gap-3 text-xl font-semibold bg-gradient-to-r from-electric-blue to-electric-purple bg-clip-text text-transparent"
          >
            <Wand2 className="w-6 h-6" />
            {language === 'he' ? 'יוצר קסם...' : 'Creating Magic...'}
          </motion.div>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-lg text-slate-300 mt-3 font-medium"
          >
            {currentStage}
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="flex items-center gap-2 mt-2 text-xs text-slate-500"
          >
            <div className="w-2 h-2 rounded-full bg-electric-blue animate-pulse" />
            {language === 'he' ? '15-30 שניות...' : '15-30 seconds...'}
          </motion.div>
        </motion.div>

        {/* Progress Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.5 }}
          className="w-full max-w-md mt-8"
        >
          <div className="relative h-3 bg-slate-800 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${generationProgress}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className="h-full bg-gradient-to-r from-electric-blue to-electric-purple rounded-full relative"
            >
              {/* Animated glow effect */}
              <motion.div
                animate={{ x: [-100, 200] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
              />
            </motion.div>
          </div>
          
          {/* Progress Steps */}
          <div className="flex justify-between mt-4 text-xs text-slate-600">
            <span className={generationProgress >= 10 ? 'text-electric-blue' : ''}>
              {language === 'he' ? 'ניתוח' : 'Analyze'}
            </span>
            <span className={generationProgress >= 40 ? 'text-electric-purple' : ''}>
              {language === 'he' ? 'דמות' : 'Character'}
            </span>
            <span className={generationProgress >= 75 ? 'text-violet-500' : ''}>
              {language === 'he' ? 'איכות' : 'Quality'}
            </span>
            <span className={generationProgress >= 100 ? 'text-green-500' : ''}>
              {language === 'he' ? 'סיום' : 'Complete'}
            </span>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* View Mode Toggle */}
      <div className="flex justify-end mb-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setViewMode(viewMode === 'split' ? 'compare' : 'split')}
          className="text-slate-400 hover:text-white"
        >
          <Layout className="w-4 h-4 me-2" />
          {viewMode === 'split' 
            ? (language === 'he' ? 'מצב השוואה' : 'Slider View')
            : (language === 'he' ? 'תצוגה מפוצלת' : 'Split View')
          }
        </Button>
      </div>

      {/* Comparison View */}
      {viewMode === 'compare' ? (
        <div className="max-w-md mx-auto">
          <ImageComparison 
            beforeImage={originalImage} 
            afterImage={generatedImage}
            overlayText={overlayText}
            watermarkText={watermarkText}
            logoUrl={logoUrl}
          />
        </div>
      ) : (
        /* Split View (Original / Generated) */
        <div className="grid md:grid-cols-2 gap-4">
          {/* Original */}
          <div className="relative rounded-2xl overflow-hidden">
            <div className="absolute top-3 left-3 z-10 px-3 py-1 rounded-full bg-black/50 text-xs font-medium text-white">
              {language === 'he' ? 'לפני' : 'Before'}
            </div>
            <img
              src={originalImage}
              alt="Original"
              className="w-full aspect-[3/4] object-cover"
            />
          </div>

          {/* Generated */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, rotate: -2 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ 
              duration: 0.8, 
              ease: [0.16, 1, 0.3, 1],
              scale: { type: "spring", stiffness: 300, damping: 25 }
            }}
            className="relative self-start rounded-2xl overflow-hidden ring-2 ring-violet-500 shadow-2xl shadow-violet-500/20"
          >
            {/* Success Badge Animation */}
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.5, type: "spring" }}
              className="absolute top-3 left-3 z-10 px-3 py-1 rounded-full bg-gradient-to-r from-violet-600 to-pink-600 text-xs font-medium text-white flex items-center gap-1"
            >
              <motion.div
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              >
                <Sparkles className="w-3 h-3" />
              </motion.div>
              {language === 'he' ? 'אחרי' : 'After'}
            </motion.div>

            {/* Image Container with Glow Effect */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="relative"
            >
              {/* Glow Effect */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: [0.5, 0.8, 0.5], scale: [1, 1.05, 1] }}
                transition={{ 
                  opacity: { duration: 3, repeat: Infinity, ease: "easeInOut" },
                  scale: { duration: 2, repeat: Infinity, ease: "easeInOut" }
                }}
                className="absolute inset-0 bg-gradient-to-br from-violet-500/20 via-transparent to-pink-500/20 rounded-2xl"
              />
              
              <img
                src={displayImage}
                alt="Generated"
                className="w-full object-contain bg-black cursor-zoom-in relative z-10"
                style={{ aspectRatio: resultAspect || '3 / 4' }}
                onLoad={(e) => {
                  const { naturalWidth, naturalHeight } = e.currentTarget;
                  if (naturalWidth && naturalHeight) setResultAspect(`${naturalWidth} / ${naturalHeight}`);
                }}
                onClick={() => {
                  setZoomLevel(1);
                  setIsZoomOpen(true);
                }}
              />
            </motion.div>

            {(overlayText || watermarkText || logoUrl) && (
              <div className="pointer-events-none absolute inset-0 flex flex-col justify-between">
                {/* Top overlay text */}
                {overlayText && (
                  <div className="flex justify-center mt-3 px-3">
                    <span className="inline-flex max-w-full items-center rounded-full bg-black/50 px-3 py-1 text-xs md:text-sm font-medium text-slate-100">
                      {overlayText}
                    </span>
                  </div>
                )}

                {/* Bottom watermark + logo */}
                <div className="flex items-end justify-between px-3 pb-3 gap-3">
                  {watermarkText && (
                    <span className="text-[10px] md:text-xs text-slate-100/80 bg-black/55 rounded-full px-2 py-1">
                      {watermarkText}
                    </span>
                  )}
                  {logoUrl && (
                    <div className="ml-auto">
                      <img
                        src={logoUrl}
                        alt="Logo watermark"
                        className="h-8 md:h-10 w-auto rounded bg-black/40 border border-white/10 object-contain"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* Character Name */}
      {characterName && (
        <div className="text-center">
          <span className="text-lg text-slate-400">{language === 'he' ? 'דמות:' : 'Character:'}</span>
          <span className="text-lg font-semibold text-white ms-2">{characterName}</span>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3 justify-center">
        <Button
          onClick={handleDownload}
          className="premium-button flex items-center gap-2"
        >
          <Download className="w-4 h-4" />
          {language === 'he' ? 'הורד' : 'Download'}
        </Button>

        {originalImage && (
          <Button
            onClick={handleDownloadOriginal}
            variant="outline"
            className="border-slate-500 text-slate-300 hover:bg-slate-700 hover:text-white flex items-center gap-2"
            title={language === 'he' ? 'הורדת התמונה המקורית שצולמה' : 'Download original captured photo'}
          >
            <Download className="w-4 h-4" />
            {language === 'he' ? 'הורד מקור' : 'Download Original'}
          </Button>
        )}
        
        <Button
          onClick={() => setShowShareModal(true)}
          variant="outline"
          className="border-electric-blue text-electric-blue hover:bg-electric-blue hover:text-white flex items-center gap-2"
        >
          <Share2 className="w-4 h-4" />
          {language === 'he' ? 'שתף' : 'Share'}
        </Button>


        <Button
          onClick={handlePrint}
          variant="outline"
          className="border-green-600 text-green-600 hover:bg-green-600 hover:text-white flex items-center gap-2"
        >
          <Printer className="w-4 h-4" />
          {language === 'he' ? 'הדפס' : 'Print'}
        </Button>

        <Button
          onClick={onTryAnother}
          variant="outline"
          className="border-slate-600 text-slate-600 hover:bg-slate-600 hover:text-white flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          {language === 'he' ? 'תמונה נוספת' : 'Try Another'}
        </Button>
      </div>

      {/* Share Modal with QR */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        imageUrl={displayImage}
        characterName={characterName}
      />

      {/* Print Configuration Modal — LumaBooth-style printer + paper picker */}
      <PrintConfigModal
        isOpen={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        imageData={displayImage}
      />


      {/* Zoom Modal */}
      {isZoomOpen && generatedImage && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center">
          <div className="relative w-full max-w-5xl max-h-[90vh] px-4">
            <button
              type="button"
              onClick={() => setIsZoomOpen(false)}
              className="absolute -top-6 right-4 text-slate-300 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="flex items-center justify-between mb-3 text-slate-200 text-sm">
              <span>{language === 'he' ? 'תצוגת זום' : 'Zoom view'}</span>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="icon"
                  className="h-8 w-8 bg-slate-800 hover:bg-slate-700 text-white"
                  onClick={() => setZoomLevel((z) => Math.max(1, z - 0.25))}
                >
                  −
                </Button>
                <span className="min-w-[3rem] text-center text-xs text-slate-300">
                  {Math.round(zoomLevel * 100)}%
                </span>
                <Button
                  type="button"
                  size="icon"
                  className="h-8 w-8 bg-slate-800 hover:bg-slate-700 text-white"
                  onClick={() => setZoomLevel((z) => Math.min(4, z + 0.25))}
                >
                  +
                </Button>
              </div>
            </div>
            <div className="overflow-auto rounded-xl bg-black max-h-[80vh]">
              <img
                src={displayImage}
                alt="Zoomed result"
                className="mx-auto"
                style={{ transform: `scale(${zoomLevel})`, transformOrigin: 'center top' }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
